import request from '@/utils/request'

/**
 * Customer Transactions API
 *
 * This module provides functions to retrieve customer-related transactions.
 * The API provides two endpoints to retrieve different types of transactions:
 * 1. Direct Customer Transactions - Transactions where customer_id is directly set
 * 2. Customer Project Transactions - Transactions from projects related to the customer
 *
 * Base URL: /api/v1/customers
 *
 * Authentication: All endpoints require authentication via Laravel Sanctum.
 * Permissions Required:
 * - view transactions - For viewing transactions
 * - view customers - For accessing customer data
 *
 * Branch Access: Users must have access to the customer's branch to use these endpoints.
 */

/**
 * List Customer Transactions
 *
 * Retrieve all transactions directly related to a customer (where customer_id matches the customer).
 *
 * @param {number} customerId - Customer ID (required)
 * @param {Object} [params={}] - Query parameters (all optional)
 * @param {string} [params.transaction_type] - Filter by type: 'income' | 'expense'
 * @param {string} [params.status] - Filter by status: 'pending' | 'approved' | 'rejected' | 'cancelled'
 * @param {string} [params.payment_source] - Filter by payment source: 'account' | 'wallet'
 * @param {string} [params.transaction_reason] - Filter by transaction reason
 * @param {string} [params.date_from] - Filter from date (format: YYYY-MM-DD)
 * @param {string} [params.date_to] - Filter to date (format: YYYY-MM-DD)
 * @param {string} [params.sort_by='created_at'] - Sort field: 'created_at' | 'amount' | 'transaction_date' | 'status'
 * @param {string} [params.sort_order='desc'] - Sort order: 'asc' | 'desc'
 * @param {number} [params.per_page=15] - Number of items per page
 * @param {number} [params.page=1] - Page number
 *
 * @returns {Promise<Object>} Response object with the following structure:
 * @returns {boolean} returns.success - Success indicator
 * @returns {string} returns.message - Response message
 * @returns {Array<Object>} returns.data - Array of transaction objects
 * @returns {Object} returns.meta - Pagination metadata (current_page, per_page, total, last_page, from, to)
 *
 * @example
 * // Get all customer expenses
 * getCustomerTransactions(1, { transaction_type: 'expense' })
 *
 * @example
 * // Get approved transactions for a date range
 * getCustomerTransactions(1, {
 *   status: 'approved',
 *   date_from: '2025-01-01',
 *   date_to: '2025-01-31'
 * })
 *
 * @example
 * // Get wallet expenses sorted by amount
 * getCustomerTransactions(1, {
 *   payment_source: 'wallet',
 *   transaction_type: 'expense',
 *   sort_by: 'amount',
 *   sort_order: 'desc'
 * })
 */
export const getCustomerTransactions = (customerId, params = {}) => request.get(`/customers/${customerId}/transactions`, { params })

/**
 * List Customer Project Transactions
 *
 * Retrieve all transactions from projects related to a customer. This includes transactions from:
 * - Projects where customer_id matches the customer (direct relationship)
 * - Projects where the customer is an owner in the project_owners pivot table (many-to-many relationship)
 *
 * @param {number} customerId - Customer ID (required)
 * @param {Object} [params={}] - Query parameters (all optional)
 * @param {string} [params.transaction_type] - Filter by type: 'income' | 'expense'
 * @param {string} [params.status] - Filter by status: 'pending' | 'approved' | 'rejected' | 'cancelled'
 * @param {string} [params.payment_source] - Filter by payment source: 'account' | 'wallet'
 * @param {string} [params.transaction_reason] - Filter by transaction reason
 * @param {string} [params.date_from] - Filter from date (format: YYYY-MM-DD)
 * @param {string} [params.date_to] - Filter to date (format: YYYY-MM-DD)
 * @param {string} [params.sort_by='created_at'] - Sort field: 'created_at' | 'amount' | 'transaction_date' | 'status'
 * @param {string} [params.sort_order='desc'] - Sort order: 'asc' | 'desc'
 * @param {number} [params.per_page=15] - Number of items per page
 * @param {number} [params.page=1] - Page number
 *
 * @returns {Promise<Object>} Response object with the following structure:
 * @returns {boolean} returns.success - Success indicator
 * @returns {string} returns.message - Response message
 * @returns {Array<Object>} returns.data - Array of transaction objects (empty array if no related projects)
 * @returns {Object} returns.meta - Pagination metadata (current_page, per_page, total, last_page, from, to)
 *
 * @example
 * // Get all approved project transactions
 * getCustomerProjectTransactions(1, { status: 'approved' })
 *
 * @example
 * // Get project income transactions for a date range
 * getCustomerProjectTransactions(1, {
 *   transaction_type: 'income',
 *   date_from: '2025-01-01',
 *   date_to: '2025-01-31'
 * })
 *
 * @example
 * // Get project transactions sorted by amount
 * getCustomerProjectTransactions(1, {
 *   transaction_type: 'income',
 *   sort_by: 'amount',
 *   sort_order: 'desc'
 * })
 *
 * @note If the customer has no related projects, an empty paginated response is returned.
 */
export const getCustomerProjectTransactions = (customerId, params = {}) => request.get(`/customers/${customerId}/project-transactions`, { params })
